<template>
    <v-row no-gutters align="start" justify="center" class="mt-3 sections-container">
        <!-- Left section -->
        <div id="left-section" class="sections">
            <!-- Socials Section -->
             <div v-if="showSocials" class="mr-2 mb-4"
                 id="socials-wrapper">
                <slot name="socials"></slot>
             </div>

            <!-- Left Cards -->
            <template v-for="card in cardsLeft">
                <HarmonyCard
                    class="mb-4"
                    :key="card.key"
                    :cardData="card"
                    :settings="settings"
                    :isEditor="isEditor"
                    :userName="userName"
                    :backendUrl="backendUrl"
                    :incompleteConfigurations="incompleteConfigurations"
                    @edit-card="$emit('edit-card', $event)"
                    @navigate-to-card="$emit('navigate-to-card', $event)"
                    @save-settings="$emit('save-settings')"
                />
            </template>

            <!-- Add Card Button Left -->
            <div 
                v-if="isEditor"
                class="card-container mr-2 rounded-xxl card-shadow d-flex justify-center align-center add-card-button"
                :class="getBase2('card')"
                @click="$emit('add-card', 'left')"
            >
                <v-icon center color="#C9C9C9" size="80px">
                    mdi-plus
                </v-icon>
            </div>


            <slot name="advertisement"></slot>
        </div>

        <!-- Right section -->
        <div id="right-section" class="sections">
            <!-- Right Cards (including wpp-email between card6 and card7) -->
            <template v-for="(card) in cardsRight">
                <!-- Insert wpp-email slot after card6 if shouldShowWppEmail -->
                <template v-if="shouldShowWppEmail && card.number === 6">
                    <slot name="wpp-email"></slot>
                </template>

                <HarmonyCard
                    :key="card.key"
                    class="mb-4"
                    :cardData="card"
                    :settings="settings"
                    :isEditor="isEditor"
                    :userName="userName"
                    :backendUrl="backendUrl"
                    :incompleteConfigurations="incompleteConfigurations"
                    @edit-card="$emit('edit-card', $event)"
                    @navigate-to-card="$emit('navigate-to-card', $event)"
                    @save-settings="$emit('save-settings')"
                />
            </template>

            <!-- Add Card Button Right -->
            <div 
                v-if="isEditor"
                class="card-container ml-2 rounded-xxl card-shadow d-flex justify-center align-center add-card-button"
                :class="getBase2('card')"
                @click="$emit('add-card', 'right')"
            >
                <v-icon center color="#C9C9C9" size="80px">
                    mdi-plus
                </v-icon>
            </div>
        </div>
    </v-row>
</template>

<script>
export default {
    name: 'LayoutClassic',
    
    components: {
        HarmonyCard: () => import('@/components/harmony/Card/HarmonyCard.vue'),
    },

    props: {
        // Array de todas las tarjetas (platforms)
        cards: {
            type: Object,
            required: true,
            // Formato esperado: { card1: {...}, card2: {...}, ... }
        },
        
        // Configuración completa del sitio
        settings: {
            type: Object,
            required: true
        },
        
        // Modo editor
        isEditor: {
            type: Boolean,
            default: false
        },
        
        // Nombre del usuario
        userName: {
            type: String,
            default: ''
        },
        
        // URL del backend
        backendUrl: {
            type: String,
            default: 'https://backend.harmony.fan/storage'
        },
        
        // Configuraciones incompletas
        incompleteConfigurations: {
            type: Array,
            default: () => []
        },

        // Mostrar sección de WhatsApp/Email
        shouldShowWppEmail: {
            type: Boolean,
            default: false
        },
        showSocials: {
            type: Boolean,
            default: false
        },
    },

    computed: {
        // Tarjetas del lado izquierdo
        cardsLeft() {
            return Object.entries(this.cards)
                .filter(([_key, value]) => value.position === 'left')
                .map(([key, value]) => ({
                    key,
                    value,
                    number: parseInt(key.replace('card', '')),
                    size: value.size || 'medium',
                    position: value.position || 'left'
                }))
                .sort((a, b) => a.number - b.number);
        },

        // Tarjetas del lado derecho
        cardsRight() {
            return Object.entries(this.cards)
                .filter(([_key, value]) => value.position === 'right')
                .map(([key, value]) => ({
                    key,
                    value,
                    number: parseInt(key.replace('card', '')),
                    size: value.size || 'medium',
                    position: value.position || 'right'
                }))
                .sort((a, b) => {
                    // Ordenar para mostrar wppOrEmail entre card5 y card6
                    if (a.number === 5) return -1;
                    if (b.number === 5) return 1;
                    return a.number - b.number;
                });
        }
    },

    methods: {
        getBase2(value) {
            // Crear cache si no existe
            this._baseCache2 = this._baseCache2 || {};
            const cacheKey = `${value}_noData`;
            
            // Verificar si ya tenemos el resultado en cache
            if (this._baseCache2[cacheKey]) return this._baseCache2[cacheKey];

            // Lookup table para resultados
            const lookupTable = {
                'card': 'card base',
                'video': 'video base',
                'card2': 'card2 base'
            };

            const result = lookupTable[value] || 'base';

            // Guardar en cache y devolver
            this._baseCache2[cacheKey] = result;
            return result;
        }
    }
}
</script>

<style scoped>
.sections {
    display: flex;
    flex-direction: column;
    width: 282px;
}

.sections-container {
    display: flex;
    flex-wrap: nowrap;
}

.add-card-button {
    background-color: #FFFFFF;
    cursor: pointer;
}

.card-shadow {
    overflow: hidden !important;
    box-shadow: #00000061 0px 1px 6px 1.3px;
}

.card-container {
    position: relative;
    height: 180px;
}

@media (max-width: 959px) {
    .sections {
        width: 260px;
    }
}

@media (max-width: 577px) {
    .sections {
        width: 260px;
    }
}

@media (max-width: 530px) {
    .sections {
        width: 240px;
    }
}

@media (max-width: 496px) {
    .sections {
        width: 225px;
    }
}

@media (max-width: 462px) {
    .sections {
        width: 208px;
    }
}

@media (max-width: 432px) {
    .sections {
        width: 199px;
    }
}

@media (max-width: 410px) {
    .sections {
        width: 188px;
    }
}

@media (max-width: 400px) {
    .sections {
        width: 180px;
    }
}

@media (max-width: 372px) {
    .sections {
        width: 170px;
    }
}

@media (max-width: 354px) {
    .sections {
        width: 160px;
    }
}

@media (max-width: 330px) {
    .sections {
        width: 150px;
    }
}

@media (max-width: 306px) {
    .sections {
        width: 140px;
    }
}
</style>